import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsSavedUser } from "@/hooks/useIsSavedUser";
import { useToggleSavedUser } from "@/hooks/useToggleSavedUser";
import { cn } from "@/lib/utils";

interface SaveUserButtonProps {
  userId: string;
  userName?: string;
  variant?: "icon" | "text";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const SaveUserButton = ({
  userId,
  userName = "user",
  variant = "icon",
  size = "icon",
  className,
}: SaveUserButtonProps) => {
  const { data: isSaved = false, isLoading } = useIsSavedUser(userId);
  const { mutate: toggleSaved, isPending } = useToggleSavedUser();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSaved({ userId, isSaved });
  };

  const isProcessing = isLoading || isPending;

  if (variant === "text") {
    return (
      <Button
        variant={isSaved ? "default" : "outline"}
        size={size}
        onClick={handleClick}
        disabled={isProcessing}
        className={className}
      >
        <Heart
          className={cn("h-4 w-4 mr-2", isSaved && "fill-current")}
        />
        {isSaved ? "Saved" : "Save"}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={handleClick}
      disabled={isProcessing}
      className={className}
      aria-label={isSaved ? `Remove ${userName} from saved` : `Save ${userName}`}
    >
      <Heart
        className={cn(
          "h-5 w-5 transition-colors",
          isSaved ? "fill-primary text-primary" : "text-muted-foreground"
        )}
      />
    </Button>
  );
};
